import proj4 from 'proj4';
import { Coordinates } from '../types';

/**
 * Utility class for handling coordinate transformations between different coordinate systems
 */
export class CoordinateTransformer {
  private static instance: CoordinateTransformer;
  private projections: Map<string, string> = new Map();

  private constructor() {
    this.setupDefaultProjections();
  }

  public static getInstance(): CoordinateTransformer {
    if (!CoordinateTransformer.instance) {
      CoordinateTransformer.instance = new CoordinateTransformer();
    }
    return CoordinateTransformer.instance;
  }

  private setupDefaultProjections(): void {
    // Common coordinate reference systems
    this.projections.set('WGS84', 'EPSG:4326');
    this.projections.set('Web Mercator', 'EPSG:3857');
    this.projections.set('UTM Zone 36N', 'EPSG:32636'); // Common for Israel
    this.projections.set('Israel TM Grid', 'EPSG:2039');

    // Define custom projections if needed
    proj4.defs('EPSG:2039', '+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +towgs84=-48,55,52,0,0,0,0 +units=m +no_defs');
  }

  /**
   * Transform coordinates from one CRS to another
   */
  private transformErrorLogged = false;

  public transform(
    coordinates: Coordinates,
    fromCRS: string,
    toCRS: string
  ): Coordinates {
    // Guard against non-finite coordinates to avoid error spam
    if (!Number.isFinite(coordinates.x) || !Number.isFinite(coordinates.y)) {
      return coordinates;
    }

    try {
      const fromProj = this.getProjection(fromCRS);
      const toProj = this.getProjection(toCRS);

      const [x, y, z] = proj4(fromProj, toProj, [
        coordinates.x,
        coordinates.y,
        coordinates.z || 0
      ]);

      return {
        x,
        y,
        z: coordinates.z !== undefined ? z : undefined,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude
      };
    } catch (error) {
      if (!this.transformErrorLogged) {
        console.warn('Coordinate transformation failed (logging once):', error);
        this.transformErrorLogged = true;
      }
      return coordinates;
    }
  }

  /**
   * Transform model coordinates to geographic coordinates
   */
  public modelToGeographic(
    modelCoords: Coordinates,
    modelOrigin: Coordinates,
    modelCRS: string = 'EPSG:2039'
  ): Coordinates {
    // Add model origin to relative coordinates
    const absoluteCoords: Coordinates = {
      x: modelCoords.x + modelOrigin.x,
      y: modelCoords.y + modelOrigin.y,
      z: (modelCoords.z || 0) + (modelOrigin.z || 0)
    };

    // Transform to WGS84
    const geoCoords = this.transform(absoluteCoords, modelCRS, 'EPSG:4326');

    return {
      ...geoCoords,
      latitude: geoCoords.y,
      longitude: geoCoords.x
    };
  }

  /**
   * Transform geographic coordinates to model coordinates
   */
  public geographicToModel(
    geoCoords: Coordinates,
    modelOrigin: Coordinates,
    modelCRS: string = 'EPSG:2039'
  ): Coordinates {
    // Use lat/lng if available, otherwise use x/y
    const inputCoords: Coordinates = {
      x: geoCoords.longitude || geoCoords.x,
      y: geoCoords.latitude || geoCoords.y,
      z: geoCoords.z
    };

    // Transform from WGS84 to model CRS
    const modelCoords = this.transform(inputCoords, 'EPSG:4326', modelCRS);

    // Subtract model origin to get relative coordinates
    return {
      x: modelCoords.x - modelOrigin.x,
      y: modelCoords.y - modelOrigin.y,
      z: modelCoords.z !== undefined ? modelCoords.z - (modelOrigin.z || 0) : undefined
    };
  }

  /**
   * Add a custom projection definition
   */
  public addProjection(name: string, definition: string): void {
    this.projections.set(name, definition);
    proj4.defs(definition);
  }

  /**
   * Get projection string for a given CRS name
   */
  private getProjection(crs: string): string {
    // If it's already an EPSG code or proj4 string, return as-is
    if (crs.startsWith('EPSG:') || crs.startsWith('+proj=')) {
      return crs;
    }

    // Look up in our predefined projections
    const projection = this.projections.get(crs);
    if (!projection) {
      throw new Error(`Unknown coordinate reference system: ${crs}`);
    }

    return projection;
  }

  /**
   * Calculate distance between two coordinates (in meters)
   */
  public calculateDistance(coord1: Coordinates, coord2: Coordinates): number {
    const R = 6371000; // Earth's radius in meters

    // Convert to radians
    const lat1Rad = (coord1.latitude || coord1.y) * Math.PI / 180;
    const lat2Rad = (coord2.latitude || coord2.y) * Math.PI / 180;
    const deltaLatRad = ((coord2.latitude || coord2.y) - (coord1.latitude || coord1.y)) * Math.PI / 180;
    const deltaLngRad = ((coord2.longitude || coord2.x) - (coord1.longitude || coord1.x)) * Math.PI / 180;

    const a = Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
              Math.cos(lat1Rad) * Math.cos(lat2Rad) *
              Math.sin(deltaLngRad / 2) * Math.sin(deltaLngRad / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Get available coordinate reference systems
   */
  public getAvailableCRS(): string[] {
    return Array.from(this.projections.keys());
  }
}